import { useEffect, useSyncExternalStore } from 'react';
import { useRouter } from 'next/router';
import {
  SCREENING_TOKEN_KEY,
  getScreeningToken,
} from 'pages/api/services/baseurl';
// import { defaultMapPath } from 'screening/orgCopy';
import { Employer, Integration, ScreeningUser } from 'screening/types';

const EMPLOYER_KEY = 'screeningEmployer';
const USER_KEY = 'screeningUser';
const INTEGRATION_KEY = 'screeningIntegration';

interface SessionData {
  token: string;
  employer: Employer;
  user: ScreeningUser;
  integration: Integration;
}

const notifySession = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('screening-session'));
};

export const setScreeningSession = ({
  token,
  employer,
  user,
  integration,
}: SessionData) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SCREENING_TOKEN_KEY, token);
  window.localStorage.setItem(EMPLOYER_KEY, JSON.stringify(employer));
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
  window.localStorage.setItem(INTEGRATION_KEY, JSON.stringify(integration));
  notifySession();
};

export const updateStoredEmployer = (employer: Employer) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(EMPLOYER_KEY, JSON.stringify(employer));
  window.dispatchEvent(new Event('screening-employer'));
  notifySession();
};

export const updateStoredIntegration = (integration: Integration) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(INTEGRATION_KEY, JSON.stringify(integration));
  notifySession();
};

export const clearScreeningSession = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(SCREENING_TOKEN_KEY);
  window.localStorage.removeItem(EMPLOYER_KEY);
  window.localStorage.removeItem(USER_KEY);
  window.localStorage.removeItem(INTEGRATION_KEY);
  notifySession();
};

const readJson = <T>(key: string): T | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export function routeAfterScreeningAuth(
  employer: Employer,
  role?: string,
  next?: string
) {
  if (employer.needs_setup && role !== 'team_lead') {
    return '/screening/onboarding';
  }
  if (next && next.startsWith('/') && !next.startsWith('//')) return next;
  // The map is held back until it is ready to ship.
  // return defaultMapPath();
  return '/screening';
}

export const getStoredEmployer = () => readJson<Employer>(EMPLOYER_KEY);
export const getStoredUser = () => readJson<ScreeningUser>(USER_KEY);
export const getStoredIntegration = () =>
  readJson<Integration>(INTEGRATION_KEY);

interface UseScreeningAuthResult {
  isReady: boolean;
  isAuthenticated: boolean;
  employer: Employer | null;
  user: ScreeningUser | null;
  integration: Integration | null;
  signOut: () => void;
}

interface SessionSnapshot {
  ready: boolean;
  authed: boolean;
  employer: Employer | null;
  user: ScreeningUser | null;
  integration: Integration | null;
}

const EMPTY_SESSION: SessionSnapshot = {
  ready: false,
  authed: false,
  employer: null,
  user: null,
  integration: null,
};

let cachedKey = '';
let cachedSession: SessionSnapshot = EMPTY_SESSION;

/** Server render stays signed-out. The client snapshot is applied before paint. */
const readClientSession = (): SessionSnapshot => {
  if (typeof window === 'undefined') return EMPTY_SESSION;
  const token = getScreeningToken() || '';
  const employerRaw = window.localStorage.getItem(EMPLOYER_KEY) || '';
  const userRaw = window.localStorage.getItem(USER_KEY) || '';
  const integrationRaw = window.localStorage.getItem(INTEGRATION_KEY) || '';
  const key = `${token}\0${employerRaw}\0${userRaw}\0${integrationRaw}`;
  if (key === cachedKey) return cachedSession;
  cachedKey = key;
  if (!token) {
    cachedSession = {
      ready: true,
      authed: false,
      employer: null,
      user: null,
      integration: null,
    };
    return cachedSession;
  }
  cachedSession = {
    ready: true,
    authed: true,
    employer: getStoredEmployer(),
    user: getStoredUser(),
    integration: getStoredIntegration(),
  };
  return cachedSession;
};

const subscribeSession = (onStoreChange: () => void) => {
  window.addEventListener('screening-session', onStoreChange);
  window.addEventListener('screening-employer', onStoreChange);
  window.addEventListener('storage', onStoreChange);
  return () => {
    window.removeEventListener('screening-session', onStoreChange);
    window.removeEventListener('screening-employer', onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
};

const serverSession = () => EMPTY_SESSION;

export const useScreeningAuth = (
  options: { redirectOnUnauthed?: boolean } = { redirectOnUnauthed: true }
): UseScreeningAuthResult => {
  const router = useRouter();
  const session = useSyncExternalStore(
    subscribeSession,
    readClientSession,
    serverSession
  );

  useEffect(() => {
    if (!session.ready || session.authed || !options.redirectOnUnauthed) return;
    const next = encodeURIComponent(router.asPath);
    if (router.pathname.startsWith('/screening/login')) return;
    router.replace(`/screening/login?next=${next}`);
  }, [session.ready, session.authed, options.redirectOnUnauthed, router]);

  return {
    isReady: session.ready,
    isAuthenticated: session.authed,
    employer: session.employer,
    user: session.user,
    integration: session.integration,
    signOut: () => {
      clearScreeningSession();
      router.replace('/screening/login');
    },
  };
};
